// src/ui/Clients.tsx – strictly admin only access
import { useEffect, useState } from "react";
import { supabase, getAuthToken } from "../lib/supabase";
import { invalidateByPrefix } from "../lib/cache";
import { useUser } from "../lib/UserContext";
import {
  buildClientsPdpaTogglePayload,
  buildPdpaOptoutEvent,
} from "../lib/pdpaOptout.mjs";
import { getNextOpenClientIds, hasSectionChanges, maskClientSecret, pickClientSectionPayload, type ClientPrimaryTab, type ClientSettingsSection, type ClientTechnicalSection } from "../lib/clientsAdminLayout.mjs";
import ClientNavigation, { ClientTechnicalSidebar } from "./clients/ClientNavigation";
import BlockedAnswers from "./BlockedAnswers";

const DAY_NAMES = ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"];
const CLINIC_CURRENCY_OPTIONS = ["SGD", "USD", "AUD", "EUR", "GBP", "MYR", "CAD", "HKD", "JPY"];

type WeeklyHours = Record<string, { open: string; close: string } | null>;

// Bot-active hours: each day holds a list of windows, so a single day can be
// split (e.g. 00:00-09:00 and 18:00-00:00). A window whose close is <= its open
// crosses midnight; 00:00-00:00 means "all day".
type DayWindow = { open: string; close: string };
type BotWeeklyHours = Record<string, DayWindow[]>;

// Accepts either the legacy shape ({ day: {open,close} | null }) or the
// multi-window shape ({ day: DayWindow[] }) and always returns clean arrays.
function normalizeBotHours(raw: unknown): BotWeeklyHours {
  const source = (raw ?? {}) as Record<string, unknown>;
  const out: BotWeeklyHours = {};
  for (let i = 0; i < 7; i++) {
    const key = String(i);
    const value = source[key];
    if (Array.isArray(value)) {
      out[key] = value.filter(
        (w): w is DayWindow => !!w && typeof w === "object" && !!(w as DayWindow).open && !!(w as DayWindow).close
      );
    } else if (value && typeof value === "object" && (value as DayWindow).open && (value as DayWindow).close) {
      out[key] = [{ open: (value as DayWindow).open, close: (value as DayWindow).close }];
    } else {
      out[key] = [];
    }
  }
  return out;
}

const DEFAULT_HOURS: WeeklyHours = {
  "0": { open: "09:00", close: "18:00" },
  "1": { open: "09:00", close: "18:00" },
  "2": { open: "09:00", close: "18:00" },
  "3": { open: "09:00", close: "18:00" },
  "4": { open: "09:00", close: "18:00" },
  "5": null,
  "6": null,
};

interface ClientRow {
  id: string;
  raison_sociale: string;
  nom: string;
  prenom: string;
  telephone: string;
  email: string;
  twilio_numero: string;
  sim_numero_serie: string;
  prompt_template: string;
  twilio_sid: string;
  twilio_auth_token: string;
  main_account_twilio: string;
  sub_account_twilio: string;
  prompt_guardrail: string;
  // Settings
  whatsapp_provider: string;
  is_label: boolean;
  assistant_label: string | null;
  ai_response_temperature: number | null;
  slot_offer_limit: number | null;
  availability_presentation: string | null;
  ical_empty_calendar_means_available: boolean;
  pdpa_consent_message: string | null;
  pdpa_consent_messages_enabled: boolean;
  pdpa_optout_requested_at: string | null;
  pdpa_optout_confirmed_at: string | null;
  pdpa_optout_confirmed_by: string | null;
  pdpa_optout_confirmed_by_name: string | null;
  pdpa_optout_attestation: string | null;
  bot_schedule_enabled: boolean;
  bot_schedule_hours: BotWeeklyHours | null;
  enable_desktop_split_view: boolean;
  default_language: string;
  currency_code: string | null;
  handler_name: string;
  // Chakra
  chakra_client_id: string;
  chakra_client_secret: string;
  chakra_refresh_token: string;
  chakra_access_token: string;
  chakra_access_token_expires_at: string;
  chakra_phone_number_id: string;
  chakra_plugin_id: string;
  chakra_api_version: string;
  // Plato appointment status
  plato_appt_status_field: string;   // "top" | "bottom" | "left" | "right"
  plato_appt_status_map: string;     // JSON string: {"YS06": "Confirmed", ...}
  // RBAC
  rbac_enabled: boolean;
  // Status tags
  status_tags_enabled: boolean;
  conversation_statuses: string; // JSON: Array<{id, label, color}>
  // Reopening digest
  enable_reopening_digest: boolean;
  enable_experience_intelligence: boolean;
  enable_quick_messages: boolean;
}

interface ClinicKnowledge {
  horaires_ouverture: WeeklyHours;
  jours_ouverts: number[];
  plato_api_key: string;
  plato_calendar_colors: string;
  plato_db_name: string;
  plato_preferred_name_custom_field: string;
  plato_doctor_name_overrides: string;
  _praticien_id?: string;
}

interface PlatoCalendar {
  id: string;
  color: string;
  name: string;
}

function parseCalendarColors(value: string | undefined): string[] {
  if (!value) return [];
  try {
    const parsed = JSON.parse(value);
    if (Array.isArray(parsed)) return parsed.map(String).map((item) => item.trim()).filter(Boolean);
  } catch {
    // The backend accepts comma-separated values as well as JSON.
  }
  return value.split(",").map((item) => item.trim()).filter(Boolean);
}

function parseDoctorNameOverrides(value: string | undefined): Record<string, string> {
  if (!value) return {};
  try {
    const parsed = JSON.parse(value);
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return {};
    return Object.fromEntries(
      Object.entries(parsed as Record<string, unknown>)
        .map(([code, name]) => [code.trim(), String(name ?? "")])
        .filter(([code, name]) => code && name.trim())
    );
  } catch {
    return {};
  }
}

function serializeDoctorNameOverrides(value: Record<string, string>): string {
  const cleaned = Object.fromEntries(
    Object.entries(value)
      .map(([code, name]) => [code.trim(), name.trim()])
      .filter(([code, name]) => code && name)
  );
  return Object.keys(cleaned).length ? JSON.stringify(cleaned) : "";
}

interface PractitionerInput {
  full_name: string;
  role: string;
}

export default function Clients() {
  const user = useUser();

  if (user === undefined) {
    return (
      <div className="flex h-screen items-center justify-center text-gray-600">
        Loading…
      </div>
    );
  }

  if (user === null || user.role !== "admin") {
    return (
      <div className="text-center text-gray-500 py-10">
        ⛔ Access restricted to administrators.
      </div>
    );
  }

  /* ---------------------------------------------------------------- */
  /* Admin                                                            */
  /* ---------------------------------------------------------------- */
  const [rows, setRows] = useState<ClientRow[]>([]);
  const [filtered, setFiltered] = useState<ClientRow[]>([]);
  const [search, setSearch] = useState("");

  /* ------------ inline editing ------------- */
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingSection, setEditingSection] = useState<string | null>(null);
  const [sectionErrors, setSectionErrors] = useState<Record<string, string>>({});
  const [draft, setDraft] = useState<Partial<ClientRow>>({});
  const [flushingId, setFlushingId] = useState<string | null>(null);
  const [flushFeedback, setFlushFeedback] = useState<Record<string, { type: "success" | "error"; message: string }>>({});

  /* ------------ clinic knowledge ------------- */
  const [knowledgeByClient, setKnowledgeByClient] = useState<Record<string, Partial<ClinicKnowledge>>>({});
  const [draftKnowledge, setDraftKnowledge] = useState<Record<string, Partial<ClinicKnowledge>>>({});
  const [platoCalendars, setPlatoCalendars] = useState<Record<string, PlatoCalendar[]>>({});
  const [platoCalendarsLoading, setPlatoCalendarsLoading] = useState<Record<string, boolean>>({});
  const [platoCalendarsErrors, setPlatoCalendarsErrors] = useState<Record<string, string>>({});

  /* ------------ praticiens ------------- */
  const [practitionerCount, setPractitionerCount] = useState<Record<string, number>>({});
  const [practitioners, setPractitioners] = useState<Record<string, PractitionerInput[]>>({});
  const [existingPractitioners, setExistingPractitioners] = useState<Record<string, any[]>>({});

  /* ------------ RBAC admin ------------- */
  const [rbacTempPasswords, setRbacTempPasswords] = useState<Record<string, string>>({});
  const [rbacResetting, setRbacResetting] = useState<string | null>(null);
  const [rbacCopied, setRbacCopied] = useState<string | null>(null);
  const [rbacAdminSaving, setRbacAdminSaving] = useState<string | null>(null);

  /* ------------ collapsible UI ------------- */
  const [addOpen, setAddOpen] = useState(false);
  const [openCards, setOpenCards] = useState<Set<string>>(new Set());
  const [activeTabs, setActiveTabs] = useState<Record<string, ClientPrimaryTab>>({});
  const [activeSettingsSections, setActiveSettingsSections] = useState<Record<string, ClientSettingsSection>>({});
  const [activeTechnicalSections, setActiveTechnicalSections] = useState<Record<string, ClientTechnicalSection>>({});

  /* ------------ delete modal ------------- */
  const [deleteTarget, setDeleteTarget] = useState<ClientRow | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const emptyNew: Partial<ClientRow> = {
    raison_sociale: "",
    nom: "",
    prenom: "",
    telephone: "",
    email: "",
    twilio_numero: "",
    sim_numero_serie: "",
    prompt_template: "",
    prompt_guardrail: "",
    twilio_sid: "",
    twilio_auth_token: "",
    main_account_twilio: "",
    sub_account_twilio: "",
  };
  const [newClient, setNewClient] = useState<Partial<ClientRow>>(emptyNew);

  /* ------------ CRUD helpers ------------- */
  async function load() {
    const { data, error } = await supabase.from("clients").select("*");
    if (!error && data) {
      setRows(data as ClientRow[]);
      setFiltered(data as ClientRow[]);
      data.forEach((c) => {
        loadPractitioners(c.id);
        loadClinicKnowledge(c.id, c.twilio_numero);
      });
    }
  }

  async function loadClinicKnowledge(clientId: string, twilioNumero: string) {
    if (!twilioNumero) return;
    const KNOWLEDGE_KEYS = [
      "horaires_ouverture",
      "jours_ouverts",
      "plato_api_key",
      "plato_calendar_colors",
      "plato_db_name",
      "plato_preferred_name_custom_field",
      "plato_doctor_name_overrides",
    ];
    const { data, error } = await supabase
      .from("clinic_knowledge")
      .select("key, value, praticien_id")
      .eq("twilio_numero", twilioNumero)
      .in("key", KNOWLEDGE_KEYS);

    if (error || !data) return;

    const parsed: Partial<ClinicKnowledge> = {};
    let praticienId: string | undefined;

    for (const row of data) {
      if (row.praticien_id && !praticienId) praticienId = row.praticien_id;
      if (row.key === "horaires_ouverture") {
        try { parsed.horaires_ouverture = JSON.parse(row.value); } catch { /* skip */ }
      } else if (row.key === "jours_ouverts") {
        try { parsed.jours_ouverts = JSON.parse(row.value); } catch { /* skip */ }
      } else if (row.key === "plato_api_key") {
        parsed.plato_api_key = row.value;
      } else if (row.key === "plato_calendar_colors") {
        parsed.plato_calendar_colors = row.value;
      } else if (row.key === "plato_db_name") {
        parsed.plato_db_name = row.value;
      } else if (row.key === "plato_preferred_name_custom_field") {
        parsed.plato_preferred_name_custom_field = row.value;
      } else if (row.key === "plato_doctor_name_overrides") {
        parsed.plato_doctor_name_overrides = row.value;
      }
    }

    if (praticienId) parsed._praticien_id = praticienId;
    setKnowledgeByClient((prev) => ({ ...prev, [clientId]: parsed }));
  }

  async function loadPlatoCalendars(clientId: string, praticienId: string | undefined, configuredColors: string) {
    if (!praticienId) {
      setPlatoCalendarsErrors((prev) => ({ ...prev, [clientId]: "Identifiant praticien Plato manquant." }));
      return;
    }
    setPlatoCalendarsLoading((prev) => ({ ...prev, [clientId]: true }));
    setPlatoCalendarsErrors((prev) => ({ ...prev, [clientId]: "" }));
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/plato/calendars`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ praticien_id: praticienId }),
      });
      const payload = await response.json();
      if (!response.ok) throw new Error(payload?.detail || "Impossible de charger les calendriers Plato.");
      const allowed = new Set(parseCalendarColors(configuredColors));
      const items = (payload.items ?? [])
        .map((item: any) => ({
          id: String(item.id ?? ""),
          color: String(item.color ?? item.raw?.color ?? "").trim(),
          name: String(item.name ?? "").trim(),
        }))
        .filter((item: PlatoCalendar) => item.color && allowed.has(item.color));
      setPlatoCalendars((prev) => ({ ...prev, [clientId]: items }));
    } catch (error) {
      setPlatoCalendarsErrors((prev) => ({
        ...prev,
        [clientId]: error instanceof Error ? error.message : "Impossible de charger les calendriers Plato.",
      }));
    } finally {
      setPlatoCalendarsLoading((prev) => ({ ...prev, [clientId]: false }));
    }
  }

  async function create() {
    const { error } = await supabase.from("clients").insert(newClient);
    if (!error) {
      setNewClient(emptyNew);
      setAddOpen(false);
      load();
    }
  }

  async function invalidateClientCaches(twilioNumero?: string) {
    const twilio = twilioNumero?.trim();
    if (!twilio) throw new Error("Missing twilio number for cache invalidation.");
    const token = await getAuthToken();
    const response = await fetch(
      "https://whatsapp-healthcare.onrender.com/internal/cache/invalidate_client",
      {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: token ?? "" },
        body: JSON.stringify({ twilio_numero: twilio }),
      }
    );
    if (!response.ok) throw new Error(`Cache invalidation failed (${response.status}).`);
  }

  async function invalidateClinicCaches(twilioNumero: string) {
    const twilio = twilioNumero?.trim();
    if (!twilio) return;
    try {
      const token = await getAuthToken();
      await fetch(
        "https://whatsapp-healthcare.onrender.com/internal/cache/invalidate_clinic",
        {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: token ?? "" },
          body: JSON.stringify({ twilio_numero: twilio }),
        }
      );
    } catch (e) {
      console.error("[CACHE] error invalidating clinic cache:", e);
    }
  }

  async function saveClinicKnowledge(clientId: string, twilioNumero: string) {
    const dk = draftKnowledge[clientId];
    if (!dk || !twilioNumero) return;

    const praticienId = knowledgeByClient[clientId]?._praticien_id;

    const toSave: Array<{ key: string; value: string; skipIfEmpty?: boolean }> = [
      {
        key: "horaires_ouverture",
        value: JSON.stringify(dk.horaires_ouverture ?? DEFAULT_HOURS),
      },
      {
        key: "jours_ouverts",
        value: JSON.stringify(dk.jours_ouverts ?? [0, 1, 2, 3, 4]),
      },
      ...[
            { key: "plato_api_key", value: dk.plato_api_key ?? "", skipIfEmpty: true },
            { key: "plato_calendar_colors", value: dk.plato_calendar_colors ?? "", skipIfEmpty: true },
            { key: "plato_db_name", value: dk.plato_db_name ?? "", skipIfEmpty: true },
            {
              key: "plato_preferred_name_custom_field",
              value: dk.plato_preferred_name_custom_field ?? "",
              skipIfEmpty: true,
            },
            {
              key: "plato_doctor_name_overrides",
              value: serializeDoctorNameOverrides(parseDoctorNameOverrides(dk.plato_doctor_name_overrides)),
            },
          ]
    ];

    for (const { key, value, skipIfEmpty } of toSave) {
      if (skipIfEmpty && !value) continue;
      await supabase
        .from("clinic_knowledge")
        .delete()
        .eq("twilio_numero", twilioNumero)
        .eq("key", key);
      if (value) {
        const row: Record<string, string> = { twilio_numero: twilioNumero, key, value };
        if (praticienId) row.praticien_id = praticienId;
        await supabase.from("clinic_knowledge").insert(row);
      }
    }
  }

  async function update(id: string) {
    const editedClient = rows.find((r) => r.id === id);

    // PDPA notices toggle: stamp/clear the opt-out audit snapshot and log an
    // event in the immutable register whenever the flag actually changes.
    let payload: Record<string, unknown> = pickClientSectionPayload(editingSection ?? "", draft as Record<string, unknown>);
    const previousPdpaEnabled = editedClient?.pdpa_consent_messages_enabled ?? true;
    const pdpaChanged =
      typeof draft.pdpa_consent_messages_enabled === "boolean" &&
      draft.pdpa_consent_messages_enabled !== previousPdpaEnabled;
    if (pdpaChanged) {
      payload = { ...payload, ...buildClientsPdpaTogglePayload(!!draft.pdpa_consent_messages_enabled) };
    }

    const sectionKey = `${id}:${editingSection ?? "unknown"}`;
    const updateResult = Object.keys(payload).length > 0
      ? await supabase.from("clients").update(payload).eq("id", id)
      : { error: null };
    if (updateResult.error) {
      setSectionErrors((prev) => ({ ...prev, [sectionKey]: updateResult.error!.message }));
      return;
    }
    setSectionErrors((prev) => {
      const next = { ...prev };
      delete next[sectionKey];
      return next;
    });

    if (pdpaChanged && user) {
      const { error: eventError } = await supabase.from("pdpa_optout_events").insert(
        buildPdpaOptoutEvent({
          clientId: id,
          action: draft.pdpa_consent_messages_enabled ? "messages_enabled" : "messages_disabled",
          actorId: user.id,
          actorRole: "admin",
          actorName: user.email,
        })
      );
      if (eventError) {
        console.error("[PDPA] register event insert failed:", eventError);
        alert("PDPA register event could not be saved: " + eventError.message);
      }
    }

    {
      invalidateByPrefix("supabase-cache:");
      try {
        const twilio = (draft.twilio_numero || editedClient?.twilio_numero) ?? "";
        await invalidateClientCaches(twilio);
        if (editingSection === "hours" || editingSection === "plato") {
          await saveClinicKnowledge(id, twilio);
        }
        await invalidateClinicCaches(twilio);
        console.log("[CACHE] caches invalidated:", twilio);
      } catch (e) {
        console.error("Erreur cache/knowledge:", e);
      }

      setEditingId(null);
      setEditingSection(null);
      setDraft({});
      setDraftKnowledge((prev) => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
      load();
    }
  }

  async function flushClientCache(client: ClientRow) {
    setFlushingId(client.id);
    setFlushFeedback((prev) => {
      const next = { ...prev };
      delete next[client.id];
      return next;
    });

    try {
      await invalidateClientCaches(client.twilio_numero);
      setFlushFeedback((prev) => ({
        ...prev,
        [client.id]: { type: "success", message: "Cache flushed successfully." },
      }));
    } catch (err) {
      console.error("[CACHE] error invalidating client cache:", err);
      setFlushFeedback((prev) => ({
        ...prev,
        [client.id]: { type: "error", message: "Failed to flush cache. Please retry." },
      }));
    } finally {
      setFlushingId(null);
    }
  }

  async function confirmDelete(client: ClientRow) {
    setDeleteLoading(true);
    try {
      const authHeader = await getAuthToken();
      if (!authHeader) {
        throw new Error("Missing authenticated session.");
      }
      const res = await fetch(
        `${import.meta.env.VITE_API_URL}/admin/clients/${client.id}`,
        { method: "DELETE", headers: { Authorization: authHeader } }
      );
      if (res.ok) {
        setDeleteTarget(null);
        invalidateByPrefix("supabase-cache:");
        load();
      } else {
        const body = await res.json().catch(() => ({}));
        alert("Erreur lors de la suppression : " + (body.detail ?? res.status));
      }
    } catch (e) {
      alert("Erreur réseau lors de la suppression.");
    } finally {
      setDeleteLoading(false);
    }
  }

  /* ------------ Practitioners CRUD ------------- */
  async function loadPractitioners(clinicId: string) {
    const { data, error } = await supabase
      .from("clinic_practitioners")
      .select("id, full_name, role, is_clinic_admin")
      .eq("clinic_id", clinicId);

    if (!error && data) {
      setExistingPractitioners((prev) => ({ ...prev, [clinicId]: data }));
    }
  }

  async function savePractitioners(clinicId: string) {
    const list = practitioners[clinicId] || [];
    if (list.length === 0) {
      alert("Please add at least one practitioner.");
      return;
    }

    const payload = list.map((p) => ({
      clinic_id: clinicId,
      full_name: p.full_name.trim(),
      role: p.role.trim() || "practitioner",
    }));

    const { error } = await supabase.from("clinic_practitioners").insert(payload);
    if (error) {
      console.error(error);
      alert("Error inserting practitioners: " + error.message);
    } else {
      alert("Practitioners added successfully!");
      setPractitionerCount((prev) => ({ ...prev, [clinicId]: 0 }));
      setPractitioners((prev) => ({ ...prev, [clinicId]: [] }));
      loadPractitioners(clinicId);
    }
  }

  async function updatePractitioner(id: string, full_name: string, role: string, clinicId: string) {
    const { error } = await supabase
      .from("clinic_practitioners")
      .update({ full_name, role })
      .eq("id", id);
    if (error) {
      alert("Update failed: " + error.message);
    } else {
      loadPractitioners(clinicId);
    }
  }

  async function deletePractitioner(id: string, clinicId: string) {
    if (!confirm("Delete this practitioner?")) return;
    const { error } = await supabase
      .from("clinic_practitioners")
      .delete()
      .eq("id", id);
    if (error) {
      alert("Delete failed: " + error.message);
    } else {
      loadPractitioners(clinicId);
    }
  }

  /* ------------ RBAC helpers ------------- */
  async function resetStaffPassword(staffId: string) {
    setRbacResetting(staffId);
    try {
      const token = await getAuthToken();
      const res = await fetch(
        `${import.meta.env.VITE_API_URL}/clinic/staff/generate-temp-password/${staffId}`,
        { method: "POST", headers: token ? { Authorization: token } : {} }
      );
      if (!res.ok) throw new Error("Failed");
      const { temp_password } = await res.json();
      setRbacTempPasswords((prev) => ({ ...prev, [staffId]: temp_password }));
    } catch {
      alert("Failed to reset password. Please try again.");
    } finally {
      setRbacResetting(null);
    }
  }

  async function setClinicAdmin(staffId: string, clinicId: string) {
    setRbacAdminSaving(staffId);
    try {
      const token = await getAuthToken();
      // First unset is_clinic_admin on all members of this clinic
      const members = existingPractitioners[clinicId] ?? [];
      for (const m of members) {
        await fetch(`${import.meta.env.VITE_API_URL}/clinic/members/${m.id}`, {
          method: "PATCH",
          headers: { "Content-Type": "application/json", ...(token ? { Authorization: token } : {}) },
          body: JSON.stringify({ is_clinic_admin: m.id === staffId }),
        });
      }
      // Reload practitioners to reflect updated state
      loadPractitioners(clinicId);
    } catch {
      alert("Failed to update clinic admin. Please try again.");
    } finally {
      setRbacAdminSaving(null);
    }
  }

  async function copyRbacPassword(staffId: string, password: string) {
    try {
      await navigator.clipboard.writeText(password);
      setRbacCopied(staffId);
      setTimeout(() => setRbacCopied(null), 2000);
    } catch { /* ignore */ }
  }

  /* ------------ effects ------------- */
  useEffect(() => {
    load();
  }, []);

  /* ------------ search ------------- */
  function onSearch(v: string) {
    setSearch(v);
    const term = v.toLowerCase();
    const f = rows.filter((c) =>
      Object.values(c).some(
        (val) => typeof val === "string" && val.toLowerCase().includes(term)
      )
    );
    setFiltered(f);
  }

  /* ------------ hours helpers ------------- */
  function updateHourDay(
    clientId: string,
    dayKey: string,
    patch: { open?: string; close?: string } | null
  ) {
    setDraftKnowledge((prev) => {
      const current = prev[clientId]?.horaires_ouverture ?? { ...DEFAULT_HOURS };
      return {
        ...prev,
        [clientId]: {
          ...prev[clientId],
          horaires_ouverture: { ...current, [dayKey]: patch },
        },
      };
    });
  }

  function setBotDayWindows(clientId: string, dayKey: string, windows: DayWindow[]) {
    setDraft((currentDraft) => {
      const client = rows.find((row) => row.id === clientId);
      const current = normalizeBotHours(
        currentDraft.bot_schedule_hours ?? client?.bot_schedule_hours ?? {}
      );
      return {
        ...currentDraft,
        bot_schedule_hours: { ...current, [dayKey]: windows },
      };
    });
  }

  // Bot-active hours default to the inverse of the clinic's opening hours. An open
  // day 09:00-18:00 becomes two windows — 00:00-09:00 and 18:00-00:00 (through
  // midnight) — and a closed day becomes one all-day window (00:00-00:00). Chained
  // across days these windows merge into continuous after-hours coverage (e.g. a
  // whole closed weekend, up to Monday's reopening). Fully editable afterwards.
  function invertHours(hours: WeeklyHours): BotWeeklyHours {
    const out: BotWeeklyHours = {};
    for (let i = 0; i < 7; i++) {
      const key = String(i);
      const day = hours[key];
      if (day && day.open && day.close && day.open !== day.close) {
        const windows: DayWindow[] = [];
        if (day.open !== "00:00") windows.push({ open: "00:00", close: day.open });
        if (day.close !== "00:00") windows.push({ open: day.close, close: "00:00" });
        out[key] = windows.length ? windows : [{ open: "00:00", close: "00:00" }];
      } else {
        out[key] = [{ open: "00:00", close: "00:00" }];
      }
    }
    return out;
  }

  function setUseClinicOpeningHours(clientId: string, useClinicHours: boolean) {
    setDraft((currentDraft) => {
      if (useClinicHours) return { ...currentDraft, bot_schedule_hours: null };
      const clinicHours =
        draftKnowledge[clientId]?.horaires_ouverture ??
        knowledgeByClient[clientId]?.horaires_ouverture ??
        DEFAULT_HOURS;
      return { ...currentDraft, bot_schedule_hours: invertHours(clinicHours) };
    });
  }

  function toggleJourOuvert(clientId: string, dayIndex: number, checked: boolean) {
    setDraftKnowledge((prev) => {
      const current = [...(prev[clientId]?.jours_ouverts ?? [])];
      const updated = checked
        ? [...current, dayIndex].sort((a, b) => a - b)
        : current.filter((d) => d !== dayIndex);
      return { ...prev, [clientId]: { ...prev[clientId], jours_ouverts: updated } };
    });
  }

  /* ---------------------------------------------------------------- */
  /* Render                                                           */
  /* ---------------------------------------------------------------- */
  return (
    <div className="max-w-6xl mx-auto px-4 py-6 font-sans text-gray-800">
      <h1 className="text-2xl font-semibold text-primary mb-6">👥 Clients Management</h1>

      {/* Add client — collapsible */}
      <section className="bg-white rounded-xl shadow mb-8 overflow-hidden">
        <button
          onClick={() => setAddOpen((v) => !v)}
          className="w-full flex items-center justify-between px-6 py-4 text-left hover:bg-gray-50 transition-colors"
        >
          <span className="text-lg font-semibold text-gray-700">➕ Add a new client</span>
          <svg
            className={`w-5 h-5 text-gray-400 transition-transform ${addOpen ? "rotate-180" : ""}`}
            fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
          </svg>
        </button>
        {addOpen && (
          <div className="px-6 pb-6 border-t">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
              {Object.entries(emptyNew).map(([k]) => (
                <div key={k}>
                  <label className="block text-sm font-medium text-gray-700 mb-1 capitalize">
                    {k.replace(/_/g, " ")}
                  </label>
                  <input
                    type="text"
                    value={(newClient as any)[k] || ""}
                    onChange={(e) => setNewClient((p) => ({ ...p, [k]: e.target.value }))}
                    className="w-full px-3 py-2 border rounded-md"
                  />
                </div>
              ))}
            </div>
            <button onClick={create} className="mt-4 px-6 py-2 bg-primary text-white rounded-md">
              Add
            </button>
          </div>
        )}
      </section>

      {/* Search */}
      <input
        value={search}
        onChange={(e) => onSearch(e.target.value)}
        placeholder="🔎 Search…"
        className="w-full mb-6 px-4 py-2 border rounded-md"
      />

      {/* List */}
      <div className="grid gap-6">
        {filtered.map((c) => {
          const dk = draftKnowledge[c.id] ?? {};
          const ck = knowledgeByClient[c.id] ?? {};
          const activeTab = activeTabs[c.id] ?? "overview";
          const activeSettingsSection = activeSettingsSections[c.id] ?? "experience-pdpa";
          const activeTechnicalSection = activeTechnicalSections[c.id] ?? "messaging";
          const currentSection = activeTab === "settings" ? activeSettingsSection : activeTab === "technical" ? activeTechnicalSection : "overview";
          const isEditing = editingId === c.id && editingSection === currentSection;
          const isDirty = isEditing && (hasSectionChanges(c, draft) || hasSectionChanges(ck, dk));
          const sectionError = sectionErrors[`${c.id}:${currentSection}`];

          const currentProvider = isEditing
            ? (draft.whatsapp_provider ?? c.whatsapp_provider ?? "twilio")
            : (c.whatsapp_provider ?? "twilio");
          const isChakra = currentProvider === "chakra";

          const currentHandler = isEditing
            ? (draft.handler_name ?? c.handler_name ?? "")
            : (c.handler_name ?? "");
          const currentAiResponseTemperature = Object.prototype.hasOwnProperty.call(draft, "ai_response_temperature")
            ? draft.ai_response_temperature
            : c.ai_response_temperature;
          const currentSlotOfferLimit = Object.prototype.hasOwnProperty.call(draft, "slot_offer_limit")
            ? draft.slot_offer_limit
            : c.slot_offer_limit;
          const currentAvailabilityPresentation = Object.prototype.hasOwnProperty.call(draft, "availability_presentation")
            ? draft.availability_presentation
            : c.availability_presentation ?? "slots";
          const currentIcalEmptyCalendarMeansAvailable = Object.prototype.hasOwnProperty.call(draft, "ical_empty_calendar_means_available")
            ? !!draft.ical_empty_calendar_means_available
            : !!c.ical_empty_calendar_means_available;
          const hasPlatoData = !!(
            ck.plato_api_key ||
            ck.plato_db_name ||
            ck.plato_calendar_colors ||
            ck.plato_preferred_name_custom_field ||
            ck.plato_doctor_name_overrides ||
            c.plato_appt_status_field ||
            c.plato_appt_status_map
          );
          const isPlato = isEditing || hasPlatoData;
          const currentPlatoCalendarColors = dk.plato_calendar_colors ?? ck.plato_calendar_colors ?? "";
          const doctorNameOverrides = parseDoctorNameOverrides(
            dk.plato_doctor_name_overrides ?? ck.plato_doctor_name_overrides
          );
          const loadedPlatoCalendars = platoCalendars[c.id] ?? [];

          const displayHours = isEditing
            ? (dk.horaires_ouverture ?? ck.horaires_ouverture ?? { ...DEFAULT_HOURS })
            : (ck.horaires_ouverture ?? {});
          const displayJours = isEditing
            ? (dk.jours_ouverts ?? ck.jours_ouverts ?? [])
            : (ck.jours_ouverts ?? []);
          const botScheduleEnabled = isEditing
            ? !!(draft.bot_schedule_enabled ?? c.bot_schedule_enabled)
            : !!c.bot_schedule_enabled;
          const botScheduleHours = Object.prototype.hasOwnProperty.call(draft, "bot_schedule_hours")
            ? draft.bot_schedule_hours
            : c.bot_schedule_hours;
          const usesClinicOpeningHours = botScheduleHours == null;
          const displayBotWindows = normalizeBotHours(botScheduleHours ?? {});

          const isOpen = openCards.has(c.id);

          function toggleCard() {
            if (isEditing) return; // don't close while editing
            setOpenCards((prev) => getNextOpenClientIds(prev, c.id));
          }

          function beginSectionEdit() {
            setEditingId(c.id);
            setEditingSection(currentSection);
            setDraft(c);
            const existing = knowledgeByClient[c.id] ?? {};
            setDraftKnowledge((prev) => ({
              ...prev,
              [c.id]: {
                horaires_ouverture: existing.horaires_ouverture ?? { ...DEFAULT_HOURS },
                jours_ouverts: existing.jours_ouverts ?? [0, 1, 2, 3, 4],
                plato_api_key: existing.plato_api_key ?? "",
                plato_calendar_colors: existing.plato_calendar_colors ?? "",
                plato_db_name: existing.plato_db_name ?? "",
                plato_preferred_name_custom_field: existing.plato_preferred_name_custom_field ?? "",
                plato_doctor_name_overrides: existing.plato_doctor_name_overrides ?? "",
                _praticien_id: existing._praticien_id,
              },
            }));
          }

          function cancelSectionEdit() {
            setEditingId(null);
            setEditingSection(null);
            setDraft({});
            setDraftKnowledge((prev) => {
              const next = { ...prev };
              delete next[c.id];
              return next;
            });
          }

          function updateDoctorNameOverride(calendarColor: string, value: string) {
            const current = parseDoctorNameOverrides(
              draftKnowledge[c.id]?.plato_doctor_name_overrides ?? knowledgeByClient[c.id]?.plato_doctor_name_overrides
            );
            const next = { ...current, [calendarColor]: value };
            setDraftKnowledge((prev) => ({
              ...prev,
              [c.id]: {
                ...prev[c.id],
                plato_doctor_name_overrides: JSON.stringify(next),
              },
            }));
          }

          function canLeaveSection() {
            return !isDirty || window.confirm("Des modifications ne sont pas enregistrées. Les abandonner ?");
          }

          return (
            <div key={c.id} className="bg-white rounded-xl shadow border overflow-hidden">
              {/* Collapsible header */}
              <button
                onClick={toggleCard}
                className={`w-full flex items-center justify-between px-6 py-4 text-left transition-colors ${!isEditing ? "hover:bg-gray-50 cursor-pointer" : "cursor-default"}`}
              >
                <div className="flex items-center gap-3 min-w-0">
                  {isEditing ? (
                    <input
                      className="border px-2 py-1 rounded text-xl font-semibold text-primary w-auto"
                      value={draft.raison_sociale ?? c.raison_sociale}
                      onChange={(e) => setDraft((d) => ({ ...d, raison_sociale: e.target.value }))}
                      onClick={(e) => e.stopPropagation()}
                    />
                  ) : (
                    <span className="text-xl font-semibold text-primary truncate">
                      {c.raison_sociale || "—"}
                    </span>
                  )}
                  {c.twilio_numero && (
                    <span className="text-sm text-gray-400 font-mono shrink-0">{c.twilio_numero}</span>
                  )}
                </div>
                {!isEditing && (
                  <svg
                    className={`w-5 h-5 text-gray-400 shrink-0 transition-transform ${isOpen ? "rotate-180" : ""}`}
                    fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                  </svg>
                )}
              </button>

              {/* Collapsible body */}
              {(isOpen || isEditing) && (
              <div className="border-t">
              <div className="flex flex-wrap gap-2 px-6 pt-4 text-xs">
                <span className="rounded-full bg-sky-50 px-2.5 py-1 text-sky-700">Fournisseur : {currentProvider}</span>
                <span className={`rounded-full px-2.5 py-1 ${botScheduleEnabled ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-600"}`}>Bot : {botScheduleEnabled ? "actif" : "inactif"}</span>
                <span className="rounded-full bg-violet-50 px-2.5 py-1 text-violet-700">Langue : {c.default_language || "—"}</span>
                <span className="rounded-full bg-slate-100 px-2.5 py-1 text-slate-700">{existingPractitioners[c.id]?.length ?? 0} praticien(s)</span>
                <span className={`rounded-full px-2.5 py-1 ${(c.pdpa_consent_messages_enabled ?? true) ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>PDPA : {(c.pdpa_consent_messages_enabled ?? true) ? "actif" : "désactivé"}</span>
                <span className={`rounded-full px-2.5 py-1 ${c.rbac_enabled ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-600"}`}>RBAC : {c.rbac_enabled ? "actif" : "inactif"}</span>
              </div>
              <ClientNavigation
                activeTab={activeTab}
                onTabChange={(tab) => { if (canLeaveSection()) { cancelSectionEdit(); setActiveTabs((prev) => ({ ...prev, [c.id]: tab })); } }}
                activeSettingsSection={activeSettingsSection}
                onSettingsSectionChange={(section) => { if (canLeaveSection()) { cancelSectionEdit(); setActiveSettingsSections((prev) => ({ ...prev, [c.id]: section })); } }}
                activeTechnicalSection={activeTechnicalSection}
                onTechnicalSectionChange={(section) => { if (canLeaveSection()) { cancelSectionEdit(); setActiveTechnicalSections((prev) => ({ ...prev, [c.id]: section })); } }}
              />
              {activeTab === "technical" && (
                <ClientTechnicalSidebar activeSection={activeTechnicalSection} onChange={(section) => { if (canLeaveSection()) { cancelSectionEdit(); setActiveTechnicalSections((prev) => ({ ...prev, [c.id]: section })); } }} />
              )}
              {currentSection !== "maintenance" && currentSection !== "practitioners" && currentSection !== "rbac" && currentSection !== "blocked-answers" && (
                <div className="flex flex-wrap items-center justify-end gap-2 px-6 pt-4">
                  {sectionError && <p role="alert" className="mr-auto rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{sectionError}</p>}
                  {isEditing ? (
                    <>
                      {isDirty && <span className="mr-auto text-xs font-medium text-amber-700">Modifications non enregistrées</span>}
                      <button type="button" onClick={cancelSectionEdit} className="rounded-lg border px-3 py-1.5 text-sm text-slate-600">Annuler</button>
                      <button type="button" onClick={() => update(c.id)} disabled={!isDirty} className="rounded-lg bg-primary px-3 py-1.5 text-sm text-white disabled:opacity-50">Enregistrer</button>
                    </>
                  ) : (
                    <button type="button" onClick={beginSectionEdit} className="rounded-lg border border-primary px-3 py-1.5 text-sm text-primary">Modifier cette section</button>
                  )}
                </div>
              )}
              <div className="px-6 pb-6 pt-5">

              {/* Main fields */}
              {activeTab === "overview" && (
              <>
              <div className="text-sm text-gray-700 grid sm:grid-cols-2 gap-x-6 gap-y-1">
                {(["nom", "prenom", "telephone", "email", "twilio_numero", "sim_numero_serie"] as (keyof ClientRow)[]).map((k) => (
                  <p key={k}>
                    <strong>{k.replace(/_/g, " ")}:</strong>{" "}
                    {isEditing ? (
                      <input
                        className="border px-2 py-1 rounded ml-1"
                        value={(draft as any)[k] ?? (c as any)[k]}
                        onChange={(e) => setDraft((d) => ({ ...d, [k]: e.target.value }))}
                      />
                    ) : (
                      k === "twilio_auth_token" ? maskClientSecret((c as any)[k]) : ((c as any)[k] || "—")
                    )}
                  </p>
                ))}
              </div>
              <div className="mt-5 grid gap-4 sm:grid-cols-2">
                <div className="rounded-xl border border-slate-200 p-4">
                  <h3 className="mb-2 font-semibold text-slate-700">Horaires</h3>
                  {DAY_NAMES.map((name, index) => {
                    const hours = displayHours[String(index)];
                    return <p key={name} className="text-sm text-slate-600"><span className="inline-block w-10 font-medium">{name}</span>{hours ? `${hours.open} – ${hours.close}` : "Fermé"}</p>;
                  })}
                </div>
                <div className="rounded-xl border border-slate-200 p-4">
                  <h3 className="mb-2 font-semibold text-slate-700">Équipe</h3>
                  {(existingPractitioners[c.id]?.length ?? 0) > 0 ? existingPractitioners[c.id].map((practitioner) => (
                    <p key={practitioner.id} className="mb-1 text-sm text-slate-600">{practitioner.full_name} · {practitioner.role || "praticien"}{practitioner.is_clinic_admin ? " · Admin clinique" : ""}</p>
                  )) : <p className="text-sm text-slate-500">Aucun praticien configuré.</p>}
                </div>
              </div>
              </>
              )}

              {/* Twilio credentials */}
              {activeTab === "technical" && activeTechnicalSection === "messaging" && (
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  {(["whatsapp_provider", "handler_name", "twilio_numero", "sim_numero_serie"] as (keyof ClientRow)[]).map((key) => (
                    <label key={key} className="text-sm text-slate-600"><span className="mb-1 block font-medium">{key.replace(/_/g, " ")}</span>{isEditing ? <input className="w-full rounded-lg border px-3 py-2" value={String(draft[key] ?? c[key] ?? "")} onChange={(event) => setDraft((current) => ({ ...current, [key]: event.target.value }))} /> : <span>{String(c[key] || "—")}</span>}</label>
                  ))}
                </div>
              )}
              {activeTab === "technical" && activeTechnicalSection === "twilio" && (
              <div className="mt-4 text-sm text-gray-700 grid sm:grid-cols-2 gap-x-6 gap-y-1 border-t pt-4">
                {(["twilio_sid", "twilio_auth_token", "main_account_twilio", "sub_account_twilio"] as (keyof ClientRow)[]).map((k) => (
                  <p key={k}>
                    <strong>{k.replace(/_/g, " ")}:</strong>{" "}
                    {isEditing ? (
                      <input
                        className="border px-2 py-1 rounded ml-1"
                        value={(draft as any)[k] ?? (c as any)[k]}
                        onChange={(e) => setDraft((d) => ({ ...d, [k]: e.target.value }))}
                      />
                    ) : (
                      (c as any)[k] || "—"
                    )}
                  </p>
                ))}
              </div>
              )}

              {/* ── Section A : Settings ───────────────────────────── */}
              {activeTab === "settings" && activeSettingsSection === "experience-pdpa" && (
              <div className="mt-4 border-t pt-4">
                <h3 className="font-semibold text-gray-700 mb-3">⚙️ Settings</h3>
                <div className="grid sm:grid-cols-2 gap-x-6 gap-y-3 text-sm">

                  {/* Provider */}
                  <div>
                    <label className="block text-gray-500 mb-1">Provider WhatsApp</label>
                    {isEditing ? (
                      <select
                        value={draft.whatsapp_provider ?? c.whatsapp_provider ?? "twilio"}
                        onChange={(e) => setDraft((d) => ({ ...d, whatsapp_provider: e.target.value }))}
                        className="border rounded px-2 py-1 w-full"
                      >
                        <option value="twilio">Twilio</option>
                        <option value="chakra">Chakra</option>
                      </select>
                    ) : (
                      <span
                        className={`inline-block px-2 py-0.5 rounded text-xs font-semibold ${
                          isChakra
                            ? "bg-green-100 text-green-700"
                            : "bg-blue-100 text-blue-700"
                        }`}
                      >
                        {c.whatsapp_provider ?? "twilio"}
                      </span>
                    )}
                  </div>

                  {/* Handler */}
                  <div>
                    <label className="block text-gray-500 mb-1">Handler</label>
                    {isEditing ? (
                      <input
                        type="text"
                        value={draft.handler_name ?? c.handler_name ?? ""}
                        onChange={(e) => setDraft((d) => ({ ...d, handler_name: e.target.value }))}
                        className="border rounded px-2 py-1 w-full"
                        placeholder="ex: plato_handler"
                      />
                    ) : (
                      <span className="font-mono text-gray-700">{c.handler_name || "—"}</span>
                    )}
                  </div>

                  {/* Default language */}
                  <div>
                    <label className="block text-gray-500 mb-1">Langue par défaut</label>
                    {isEditing ? (
                      <input
                        type="text"
                        value={draft.default_language ?? c.default_language ?? ""}
                        onChange={(e) => setDraft((d) => ({ ...d, default_language: e.target.value }))}
                        className="border rounded px-2 py-1 w-full"
                        placeholder="fr / en / zh / ms…"
                      />
                    ) : (
                      <span>{c.default_language || "—"}</span>
                    )}
                  </div>

                  <div>
                    <label className="block text-gray-500 mb-1">Clinic currency</label>
                    {isEditing ? (
                      <select
                        value={draft.currency_code ?? c.currency_code ?? ""}
                        onChange={(e) =>
                          setDraft((d) => ({
                            ...d,
                            currency_code: e.target.value || null,
                          }))
                        }
                        className="border rounded px-2 py-1 w-full"
                      >
                        <option value="">Not configured</option>
                        {CLINIC_CURRENCY_OPTIONS.map((currencyCode) => (
                          <option key={currencyCode} value={currencyCode}>
                            {currencyCode}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <span>{c.currency_code || "Not configured"}</span>
                    )}
                  </div>

                  {/* AI response temperature */}
                  <div>
                    <label className="block text-gray-500 mb-1">AI response temperature</label>
                    {isEditing ? (
                      <input
                        type="number"
                        min={0}
                        max={2}
                        step={0.05}
                        value={currentAiResponseTemperature ?? ""}
                        onChange={(e) =>
                          setDraft((d) => ({
                            ...d,
                            ai_response_temperature: e.target.value === "" ? null : Number(e.target.value),
                          }))
                        }
                        className="border rounded px-2 py-1 w-full"
                        placeholder="Render default"
                      />
                    ) : (
                      <span>
                        {c.ai_response_temperature == null ? "Render default" : c.ai_response_temperature}
                      </span>
                    )}
                  </div>

                  {/* PDPA consent message */}
                  <div className="sm:col-span-2">
                    <label className="block text-gray-500 mb-1">PDPA consent message</label>
                    {isEditing ? (
                      <textarea
                        value={draft.pdpa_consent_message ?? c.pdpa_consent_message ?? ""}
                        onChange={(e) =>
                          setDraft((d) => ({ ...d, pdpa_consent_message: e.target.value }))
                        }
                        rows={3}
                        className="border rounded px-2 py-1 w-full"
                        placeholder="Use the default PDPA message"
                      />
                    ) : (
                      <p className="text-gray-700 whitespace-pre-line">
                        {c.pdpa_consent_message?.trim() || "Default PDPA message"}
                      </p>
                    )}
                  </div>

                  {/* Toggles */}
                  <div className="flex flex-col gap-2">
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        disabled={!isEditing}
                        checked={isEditing ? !!(draft.is_label ?? c.is_label) : !!c.is_label}
                        onChange={(e) => setDraft((d) => ({ ...d, is_label: e.target.checked }))}
                        className="w-4 h-4"
                      />
                      <span className="text-gray-700">Label praticiens (is_label)</span>
                    </label>
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        disabled={!isEditing}
                        checked={
                          isEditing
                            ? (draft.pdpa_consent_messages_enabled ?? c.pdpa_consent_messages_enabled ?? true)
                            : (c.pdpa_consent_messages_enabled ?? true)
                        }
                        onChange={(e) =>
                          setDraft((d) => ({ ...d, pdpa_consent_messages_enabled: e.target.checked }))
                        }
                        className="w-4 h-4"
                      />
                      <span className="text-gray-700">
                        PDPA consent messages
                        {(c.pdpa_consent_messages_enabled ?? true) ? (
                          <span className="ml-2 text-xs bg-sky-100 text-sky-700 px-1.5 py-0.5 rounded font-medium">
                            Active
                          </span>
                        ) : c.pdpa_optout_confirmed_at ? (
                          <span className="ml-2 text-xs bg-green-100 text-green-700 px-1.5 py-0.5 rounded font-medium">
                            Opt-out confirmed by clinic
                          </span>
                        ) : (
                          <span className="ml-2 text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded font-medium">
                            Awaiting clinic confirmation
                          </span>
                        )}
                      </span>
                    </label>
                    <div className="pl-6">
                      <label className="block text-gray-500 mb-1">Assistant label</label>
                      {isEditing ? (
                        <input
                          type="text"
                          value={draft.assistant_label ?? c.assistant_label ?? ""}
                          onChange={(e) => setDraft((d) => ({ ...d, assistant_label: e.target.value }))}
                          className="border rounded px-2 py-1 w-full"
                          placeholder="AI assistant"
                        />
                      ) : (
                        <span className="text-gray-700">
                          {c.assistant_label?.trim() || "AI assistant"}
                        </span>
                      )}
                    </div>
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        disabled={!isEditing}
                        checked={
                          isEditing
                            ? !!(draft.enable_desktop_split_view ?? c.enable_desktop_split_view)
                            : !!c.enable_desktop_split_view
                        }
                        onChange={(e) =>
                          setDraft((d) => ({ ...d, enable_desktop_split_view: e.target.checked }))
                        }
                        className="w-4 h-4"
                      />
                      <span className="text-gray-700">Split view desktop</span>
                    </label>
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        disabled={!isEditing}
                        checked={
                          isEditing
                            ? !!(draft.rbac_enabled ?? c.rbac_enabled)
                            : !!c.rbac_enabled
                        }
                        onChange={(e) =>
                          setDraft((d) => ({ ...d, rbac_enabled: e.target.checked }))
                        }
                        className="w-4 h-4"
                      />
                      <span className="text-gray-700">
                        RBAC — staff PIN + permissions
                        {c.rbac_enabled && (
                          <span className="ml-2 text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded font-medium">
                            Active
                          </span>
                        )}
                      </span>
                    </label>
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        disabled={!isEditing}
                        checked={
                          isEditing
                            ? !!(draft.status_tags_enabled ?? c.status_tags_enabled)
                            : !!c.status_tags_enabled
                        }
                        onChange={(e) =>
                          setDraft((d) => ({ ...d, status_tags_enabled: e.target.checked }))
                        }
                        className="w-4 h-4"
                      />
                      <span className="text-gray-700">
                        Conversation labels
                        {c.status_tags_enabled && (
                          <span className="ml-2 text-xs bg-green-100 text-green-700 px-1.5 py-0.5 rounded font-medium">
                            Active
                          </span>
                        )}
                      </span>
                    </label>
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        disabled={!isEditing}
                        checked={
                          isEditing
                            ? !!(draft.enable_reopening_digest ?? c.enable_reopening_digest)
                            : !!c.enable_reopening_digest
                        }
                        onChange={(e) =>
                          setDraft((d) => ({ ...d, enable_reopening_digest: e.target.checked }))
                        }
                        className="w-4 h-4"
                      />
                      <span className="text-gray-700">
                        Reopening digest
                        {c.enable_reopening_digest && (
                          <span className="ml-2 text-xs bg-teal-100 text-teal-700 px-1.5 py-0.5 rounded font-medium">
                            Active
                          </span>
                        )}
                      </span>
                    </label>
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        disabled={!isEditing}
                        checked={
                          isEditing
                            ? !!(draft.enable_experience_intelligence ?? c.enable_experience_intelligence)
                            : !!c.enable_experience_intelligence
                        }
                        onChange={(e) =>
                          setDraft((d) => ({ ...d, enable_experience_intelligence: e.target.checked }))
                        }
                        className="w-4 h-4"
                      />
                      <span className="text-gray-700">
                        Clinic insights
                        {c.enable_experience_intelligence && (
                          <span className="ml-2 text-xs bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded font-medium">
                            Active
                          </span>
                        )}
                      </span>
                    </label>
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        disabled={!isEditing}
                        checked={
                          isEditing
                            ? !!(draft.enable_quick_messages ?? c.enable_quick_messages)
                            : !!c.enable_quick_messages
                        }
                        onChange={(e) =>
                          setDraft((d) => ({ ...d, enable_quick_messages: e.target.checked }))
                        }
                        className="w-4 h-4"
                      />
                      <span className="text-gray-700">
                        Quick messages
                        {c.enable_quick_messages && (
                          <span className="ml-2 text-xs bg-indigo-100 text-indigo-700 px-1.5 py-0.5 rounded font-medium">
                            Active
                          </span>
                        )}
                      </span>
                    </label>
                  </div>
                </div>
              </div>
              )}

              {/* ── Section B : Chakra (conditionnel) ─────────────── */}
              {activeTab === "technical" && activeTechnicalSection === "chakra" && isChakra && (
                <div className="mt-4 border-t pt-4">
                  <h3 className="font-semibold text-gray-700 mb-3">📱 Chakra Configuration</h3>
                  <div className="grid sm:grid-cols-2 gap-x-6 gap-y-3 text-sm">
                    {(
                      [
                        ["chakra_client_id", "Client ID"],
                        ["chakra_client_secret", "Client Secret"],
                        ["chakra_phone_number_id", "Phone Number ID"],
                        ["chakra_plugin_id", "Plugin ID"],
                        ["chakra_api_version", "API Version"],
                        ["chakra_access_token_expires_at", "Access Token Expires At"],
                      ] as [keyof ClientRow, string][]
                    ).map(([k, label]) => (
                      <div key={k}>
                        <label className="block text-gray-500 mb-1">{label}</label>
                        {isEditing ? (
                          <input
                            type="text"
                            value={(draft as any)[k] ?? (c as any)[k] ?? ""}
                            onChange={(e) => setDraft((d) => ({ ...d, [k]: e.target.value }))}
                            className="border rounded px-2 py-1 w-full font-mono text-xs"
                          />
                        ) : (
                          <span className="font-mono text-xs break-all">
                            {k === "chakra_client_secret" ? maskClientSecret((c as any)[k]) : ((c as any)[k] || "—")}
                          </span>
                        )}
                      </div>
                    ))}
                    {/* Tokens on full width */}
                    {(
                      [
                        ["chakra_refresh_token", "Refresh Token"],
                        ["chakra_access_token", "Access Token"],
                      ] as [keyof ClientRow, string][]
                    ).map(([k, label]) => (
                      <div key={k} className="sm:col-span-2">
                        <label className="block text-gray-500 mb-1">{label}</label>
                        {isEditing ? (
                          <input
                            type="text"
                            value={(draft as any)[k] ?? (c as any)[k] ?? ""}
                            onChange={(e) => setDraft((d) => ({ ...d, [k]: e.target.value }))}
                            className="border rounded px-2 py-1 w-full font-mono text-xs"
                          />
                        ) : (
                          <span className="font-mono text-xs break-all">
                            {maskClientSecret((c as any)[k])}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {activeTab === "settings" && activeSettingsSection === "blocked-answers" && (
                <BlockedAnswers clientId={c.id} embedded />
              )}

              {/* ── Section C : Horaires d'ouverture ──────────────── */}
              {activeTab === "settings" && activeSettingsSection === "hours" && (
              <div className="mt-4 border-t pt-4">
                <h3 className="font-semibold text-gray-700 mb-3">🕐 Horaires d'ouverture</h3>

                {/* jours_ouverts */}
                <div className="mb-3">
                  <p className="text-xs text-gray-500 mb-1">Jours ouverts</p>
                  <div className="flex flex-wrap gap-3">
                    {DAY_NAMES.map((name, i) => {
                      const isOpen = displayJours.includes(i);
                      return (
                        <label key={i} className="flex items-center gap-1 text-sm">
                          <input
                            type="checkbox"
                            disabled={!isEditing}
                            checked={isOpen}
                            onChange={(e) => toggleJourOuvert(c.id, i, e.target.checked)}
                            className="w-4 h-4"
                          />
                          <span className={isOpen ? "text-gray-800" : "text-gray-400"}>{name}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>

                {/* horaires_ouverture */}
                <div className="space-y-1">
                  {Array.from({ length: 7 }, (_, i) => {
                    const key = String(i);
                    const dayHours = displayHours[key];
                    const isOpen = dayHours !== null && dayHours !== undefined;
                    return (
                      <div key={i} className="flex items-center gap-2 text-sm">
                        <span className="w-8 font-medium text-gray-700 shrink-0">
                          {DAY_NAMES[i]}
                        </span>
                        {isEditing ? (
                          <>
                            <input
                              type="checkbox"
                              checked={isOpen}
                              onChange={(e) =>
                                updateHourDay(
                                  c.id,
                                  key,
                                  e.target.checked ? { open: "09:00", close: "18:00" } : null
                                )
                              }
                              className="w-4 h-4"
                            />
                            {isOpen ? (
                              <>
                                <input
                                  type="time"
                                  value={dayHours!.open}
                                  onChange={(e) =>
                                    updateHourDay(c.id, key, { ...dayHours!, open: e.target.value })
                                  }
                                  className="border rounded px-2 py-0.5 text-sm"
                                />
                                <span className="text-gray-400">–</span>
                                <input
                                  type="time"
                                  value={dayHours!.close}
                                  onChange={(e) =>
                                    updateHourDay(c.id, key, { ...dayHours!, close: e.target.value })
                                  }
                                  className="border rounded px-2 py-0.5 text-sm"
                                />
                              </>
                            ) : (
                              <span className="text-gray-400 text-xs">Fermé</span>
                            )}
                          </>
                        ) : (
                          <span className={isOpen ? "text-gray-700" : "text-gray-400 text-xs"}>
                            {isOpen ? `${dayHours!.open} – ${dayHours!.close}` : "Fermé"}
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
              )}

              {/* ── Section D : Plato (conditionnel) ──────────────── */}
              {activeTab === "settings" && activeSettingsSection === "automation" && (
              <div className="mt-4 border-t pt-4">
                <h3 className="font-semibold text-gray-700 mb-2">Bot automation</h3>
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    disabled={!isEditing}
                    checked={botScheduleEnabled}
                    onChange={(e) =>
                      setDraft((currentDraft) => ({
                        ...currentDraft,
                        bot_schedule_enabled: e.target.checked,
                      }))
                    }
                    className="w-4 h-4"
                  />
                  <span>Automate bot according to schedule</span>
                </label>

                {botScheduleEnabled && (
                  <div className="mt-3 rounded border border-sky-100 bg-sky-50/50 p-3">
                    <p className="mb-3 text-xs text-gray-600">
                      The bot answers patients <strong>only during the windows below</strong>. Outside them,
                      replies are left to the staff. A window may cross midnight (e.g. <strong>18:00 → 09:00</strong>),
                      a day may hold <strong>several windows</strong>, and consecutive windows join up — so an
                      after-hours default covers whole closed weekends through to reopening.
                    </p>
                    {isEditing ? (
                      <label className="flex items-center gap-2 text-sm text-gray-700">
                        <input
                          type="checkbox"
                          checked={usesClinicOpeningHours}
                          onChange={(e) => setUseClinicOpeningHours(c.id, e.target.checked)}
                          className="w-4 h-4"
                        />
                        <span>Bot active during clinic opening hours</span>
                      </label>
                    ) : (
                      <p className="text-sm text-gray-700">
                        {usesClinicOpeningHours ? "Bot active during clinic opening hours" : "Custom bot active hours"}
                      </p>
                    )}

                    {isEditing && !usesClinicOpeningHours && (
                      <div className="mt-3">
                        <p className="mb-2 text-xs font-medium text-gray-600">Hours when the bot answers</p>
                        <div className="space-y-2">
                          {Array.from({ length: 7 }, (_, i) => {
                            const dayKey = String(i);
                            const windows = displayBotWindows[dayKey] ?? [];
                            const active = windows.length > 0;
                            return (
                              <div key={dayKey} className="flex items-start gap-2 text-sm">
                                <span className="w-8 shrink-0 pt-1 font-medium text-gray-700">{DAY_NAMES[i]}</span>
                                <input
                                  type="checkbox"
                                  checked={active}
                                  onChange={(e) =>
                                    setBotDayWindows(
                                      c.id,
                                      dayKey,
                                      e.target.checked ? [{ open: "18:00", close: "09:00" }] : []
                                    )
                                  }
                                  className="mt-1 h-4 w-4"
                                />
                                <div className="flex flex-col gap-1">
                                  {active ? (
                                    windows.map((w, idx) => (
                                      <div key={idx} className="flex items-center gap-2">
                                        <input
                                          type="time"
                                          value={w.open}
                                          onChange={(e) =>
                                            setBotDayWindows(
                                              c.id,
                                              dayKey,
                                              windows.map((x, j) => (j === idx ? { ...x, open: e.target.value } : x))
                                            )
                                          }
                                          className="rounded border px-2 py-0.5 text-sm"
                                        />
                                        <span className="text-gray-400">–</span>
                                        <input
                                          type="time"
                                          value={w.close}
                                          onChange={(e) =>
                                            setBotDayWindows(
                                              c.id,
                                              dayKey,
                                              windows.map((x, j) => (j === idx ? { ...x, close: e.target.value } : x))
                                            )
                                          }
                                          className="rounded border px-2 py-0.5 text-sm"
                                        />
                                        {w.open === "00:00" && w.close === "00:00" ? (
                                          <span className="text-xs text-emerald-600">all day</span>
                                        ) : w.close <= w.open ? (
                                          <span className="text-xs text-amber-600">next day</span>
                                        ) : null}
                                        <button
                                          type="button"
                                          onClick={() =>
                                            setBotDayWindows(
                                              c.id,
                                              dayKey,
                                              windows.filter((_, j) => j !== idx)
                                            )
                                          }
                                          className="px-1 text-xs text-gray-400 hover:text-red-500"
                                          aria-label="Remove window"
                                        >
                                          ✕
                                        </button>
                                      </div>
                                    ))
                                  ) : (
                                    <span className="pt-1 text-xs text-gray-400">Bot off</span>
                                  )}
                                  {active && (
                                    <button
                                      type="button"
                                      onClick={() =>
                                        setBotDayWindows(c.id, dayKey, [...windows, { open: "18:00", close: "09:00" }])
                                      }
                                      className="text-left text-xs text-sky-600 hover:text-sky-700"
                                    >
                                      + add window
                                    </button>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
              )}

              {activeTab === "technical" && activeTechnicalSection === "plato" && isPlato && (
                <div className="mt-4 border-t pt-4">
                  <h3 className="font-semibold text-gray-700 mb-3">🏥 Plato Configuration</h3>
                  <div className="grid sm:grid-cols-2 gap-x-6 gap-y-3 text-sm">
                    {(
                      [
                        ["plato_api_key", "API Key"],
                        ["plato_db_name", "DB Name"],
                      ] as const
                    ).map(([k, label]) => (
                      <div key={k}>
                        <label className="block text-gray-500 mb-1">{label}</label>
                        {isEditing ? (
                          <input
                            type="text"
                            value={(dk as any)[k] ?? (ck as any)[k] ?? ""}
                            onChange={(e) =>
                              setDraftKnowledge((prev) => ({
                                ...prev,
                                [c.id]: { ...prev[c.id], [k]: e.target.value },
                              }))
                            }
                            className="border rounded px-2 py-1 w-full font-mono text-xs"
                          />
                        ) : (
                          <span className="font-mono text-xs break-all">
                            {k === "plato_api_key" ? maskClientSecret((ck as any)[k]) : ((ck as any)[k] || "—")}
                          </span>
                        )}
                      </div>
                    ))}
                    <div className="sm:col-span-2">
                      <label className="block text-gray-500 mb-1">Calendar Colors (JSON)</label>
                      {isEditing ? (
                        <input
                          type="text"
                          value={dk.plato_calendar_colors ?? ck.plato_calendar_colors ?? ""}
                          onChange={(e) =>
                            setDraftKnowledge((prev) => ({
                              ...prev,
                              [c.id]: { ...prev[c.id], plato_calendar_colors: e.target.value },
                            }))
                          }
                          className="border rounded px-2 py-1 w-full font-mono text-xs"
                          placeholder='["#FF5733","#33FF57",...]'
                        />
                      ) : (
                        <span className="font-mono text-xs break-all">
                          {ck.plato_calendar_colors || "—"}
                        </span>
                      )}
                    </div>

                    <div className="sm:col-span-2 border-t pt-3">
                      <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                        <div>
                          <p className="font-medium text-gray-700">Noms des médecins Plato</p>
                          <p className="text-xs text-gray-500">
                            Le nom Plato est utilisé par défaut si aucun nom personnalisé n’est renseigné.
                          </p>
                        </div>
                        {isEditing && (
                          <button
                            type="button"
                            onClick={() => loadPlatoCalendars(c.id, dk._praticien_id ?? ck._praticien_id, currentPlatoCalendarColors)}
                            disabled={platoCalendarsLoading[c.id]}
                            className="border border-sky-600 text-sky-700 px-3 py-1.5 rounded-md text-xs disabled:opacity-60"
                          >
                            {platoCalendarsLoading[c.id] ? "Chargement…" : "Charger les calendriers Plato"}
                          </button>
                        )}
                      </div>
                      {platoCalendarsErrors[c.id] && (
                        <p className="text-xs text-red-600 mb-2">{platoCalendarsErrors[c.id]}</p>
                      )}
                      {loadedPlatoCalendars.length > 0 ? (
                        <div className="space-y-2">
                          {loadedPlatoCalendars.map((calendar) => (
                            <div key={calendar.color} className="grid sm:grid-cols-[7rem_1fr_1fr] gap-2 items-center text-xs">
                              <code className="font-mono text-gray-600">{calendar.color}</code>
                              <span>
                                <span className="block text-[11px] text-gray-400">Nom Plato</span>
                                <span className="font-medium text-gray-700">{calendar.name || "Nom non fourni"}</span>
                              </span>
                              {isEditing ? (
                                <label>
                                  <span className="block text-[11px] text-gray-400">Nom utilisé dans les templates</span>
                                  <input
                                    type="text"
                                    value={doctorNameOverrides[calendar.color] ?? ""}
                                    onChange={(event) => updateDoctorNameOverride(calendar.color, event.target.value)}
                                    placeholder={calendar.name || "Nom Plato par défaut"}
                                    className="border rounded px-2 py-1 w-full text-xs"
                                  />
                                </label>
                              ) : (
                                <span>
                                  <span className="block text-[11px] text-gray-400">Nom utilisé dans les templates</span>
                                  <span className="font-medium text-gray-700">{doctorNameOverrides[calendar.color] || calendar.name || "—"}</span>
                                </span>
                              )}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-xs text-gray-400">Cliquez sur le bouton pour charger les calendriers configurés.</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-gray-500 mb-1">Preferred Name Custom Field</label>
                      {isEditing ? (
                        <input
                          type="text"
                          value={
                            dk.plato_preferred_name_custom_field ??
                            ck.plato_preferred_name_custom_field ??
                            ""
                          }
                          onChange={(e) =>
                            setDraftKnowledge((prev) => ({
                              ...prev,
                              [c.id]: {
                                ...prev[c.id],
                                plato_preferred_name_custom_field: e.target.value,
                              },
                            }))
                          }
                          className="border rounded px-2 py-1 w-full font-mono text-xs"
                          placeholder="dsBe"
                        />
                      ) : (
                        <span className="font-mono text-xs break-all">
                          {ck.plato_preferred_name_custom_field || "Not configured"}
                        </span>
                      )}
                    </div>

                    {/* Appointment status field */}
                    <div>
                      <label className="block text-gray-500 mb-1">Appointment Status Code</label>
                      {isEditing ? (
                        <select
                          value={draft.plato_appt_status_field ?? c.plato_appt_status_field ?? ""}
                          onChange={(e) => setDraft((d) => ({ ...d, plato_appt_status_field: e.target.value }))}
                          className="border rounded px-2 py-1 w-full text-xs"
                        >
                          <option value="">— Non configuré —</option>
                          <option value="top">code_Top</option>
                          <option value="bottom">code_Bottom</option>
                          <option value="left">code_Left</option>
                          <option value="right">code_Right</option>
                        </select>
                      ) : (
                        <span className="font-mono text-xs">
                          {c.plato_appt_status_field
                            ? `code_${c.plato_appt_status_field.charAt(0).toUpperCase() + c.plato_appt_status_field.slice(1)}`
                            : "—"}
                        </span>
                      )}
                    </div>

                    {/* Appointment status map */}
                    <div>
                      <label className="block text-gray-500 mb-1">Status Map (JSON)</label>
                      {isEditing ? (
                        <textarea
                          value={
                            typeof draft.plato_appt_status_map === "object" && draft.plato_appt_status_map !== null
                              ? JSON.stringify(draft.plato_appt_status_map, null, 2)
                              : (draft.plato_appt_status_map as string) ?? (
                                  typeof c.plato_appt_status_map === "object" && c.plato_appt_status_map !== null
                                    ? JSON.stringify(c.plato_appt_status_map, null, 2)
                                    : (c.plato_appt_status_map as string) ?? ""
                                )
                          }
                          onChange={(e) => setDraft((d) => ({ ...d, plato_appt_status_map: e.target.value as any }))}
                          rows={3}
                          className="border rounded px-2 py-1 w-full font-mono text-xs"
                          placeholder='{"YS06": "Confirmed", "YS07": "Cancelled"}'
                        />
                      ) : (
                        <span className="font-mono text-xs break-all">
                          {c.plato_appt_status_map
                            ? (typeof c.plato_appt_status_map === "object"
                                ? JSON.stringify(c.plato_appt_status_map)
                                : c.plato_appt_status_map)
                            : "—"}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Prompt field */}
              {activeTab === "technical" && activeTechnicalSection === "ai-prompts" && (
              <>
              <div className="mb-4 text-sm text-gray-600">
                <strong>Température de réponse IA :</strong>{" "}
                {isEditing ? <input type="number" min={0} max={2} step={0.05} value={currentAiResponseTemperature ?? ""} onChange={(event) => setDraft((current) => ({ ...current, ai_response_temperature: event.target.value === "" ? null : Number(event.target.value) }))} className="ml-2 w-28 rounded border px-2 py-1" /> : <span>{c.ai_response_temperature == null ? "Valeur Render par défaut" : c.ai_response_temperature}</span>}
              </div>
              <div className="mb-4 text-sm text-gray-600">
                <strong>Créneaux proposés au patient :</strong>{" "}
                {isEditing ? <input type="number" min={1} max={10} step={1} value={currentSlotOfferLimit ?? ""} onChange={(event) => {
                  if (event.target.value === "") {
                    setDraft((current) => ({ ...current, slot_offer_limit: null }));
                    return;
                  }
                  const slotOfferLimit = Number(event.target.value);
                  if (Number.isInteger(slotOfferLimit) && slotOfferLimit >= 1 && slotOfferLimit <= 10) {
                    setDraft((current) => ({ ...current, slot_offer_limit: slotOfferLimit }));
                  }
                }} className="ml-2 w-28 rounded border px-2 py-1" placeholder="3" /> : <span>{c.slot_offer_limit == null ? 3 : c.slot_offer_limit}{c.slot_offer_limit == null ? " (default)" : ""}</span>}
              </div>
              <div className="mb-4 border-t pt-4 text-sm text-gray-600">
                <strong>iCal availability presentation:</strong>{" "}
                {isEditing ? (
                  <select
                    value={currentAvailabilityPresentation ?? "slots"}
                    onChange={(event) => setDraft((current) => ({
                      ...current,
                      availability_presentation: event.target.value,
                    }))}
                    className="ml-2 rounded border px-2 py-1"
                  >
                    <option value="slots">Appointment times</option>
                    <option value="windows">Free iCal time windows</option>
                  </select>
                ) : (
                  <span>{currentAvailabilityPresentation === "windows" ? "Free iCal time windows" : "Appointment times"}</span>
                )}
                <p className="mt-2 text-xs text-gray-500">
                  Jane remains the booking authority for the final appointment.
                </p>
                <label className="mt-2 flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={currentIcalEmptyCalendarMeansAvailable}
                    disabled={!isEditing || currentAvailabilityPresentation !== "windows"}
                    onChange={(event) => setDraft((current) => ({
                      ...current,
                      ical_empty_calendar_means_available: event.target.checked,
                    }))}
                  />
                  Treat an empty iCal workday as available
                </label>
              </div>
              <div className="mt-4 text-sm text-gray-600 whitespace-pre-line border-t pt-4">
                <strong>Prompt:</strong>
                {isEditing ? (
                  <textarea
                    className="w-full border px-2 py-1 rounded mt-1"
                    value={draft.prompt_template ?? c.prompt_template}
                    onChange={(e) => setDraft((d) => ({ ...d, prompt_template: e.target.value }))}
                  />
                ) : (
                  <p className="mt-1">{c.prompt_template}</p>
                )}
              </div>

              {/* Prompt Guardrail field */}
              <div className="mt-4 text-sm text-gray-600 whitespace-pre-line">
                <strong>Prompt Guardrail:</strong>
                {isEditing ? (
                  <textarea
                    className="w-full border px-2 py-1 rounded mt-1"
                    value={draft.prompt_guardrail ?? c.prompt_guardrail}
                    onChange={(e) =>
                      setDraft((d) => ({ ...d, prompt_guardrail: e.target.value }))
                    }
                    rows={6}
                  />
                ) : (
                  <p className="mt-1">{c.prompt_guardrail || "—"}</p>
                )}
              </div>
              </>
              )}

              {/* Actions */}
              {activeTab === "technical" && activeTechnicalSection === "maintenance" && (
              <>
              <div className="flex gap-2 mt-4">
                <button onClick={() => setDeleteTarget(c)} className="text-red-600 border border-red-600 px-4 py-1 rounded-md text-sm">Supprimer le client</button>
                <button onClick={() => flushClientCache(c)} disabled={flushingId === c.id} className="text-amber-700 border border-amber-700 px-4 py-1 rounded-md text-sm disabled:opacity-60 disabled:cursor-not-allowed">{flushingId === c.id ? "Vidage…" : "Vider le cache"}</button>
              </div>
              {flushFeedback[c.id] && (
                <p
                  className={`mt-2 text-sm ${
                    flushFeedback[c.id].type === "success" ? "text-green-600" : "text-red-600"
                  }`}
                >
                  {flushFeedback[c.id].message}
                </p>
              )}
              </>
              )}

              {/* Section for practitioners */}
              {activeTab === "settings" && ["practitioners", "rbac"].includes(activeSettingsSection) && (
              <>
              {activeSettingsSection === "practitioners" && (
              <div className="mt-6 border-t pt-4">
                <h3 className="font-semibold text-gray-700 mb-2">
                  Add practitioners for this clinic
                </h3>

                <label className="block text-sm text-gray-600 mb-2">
                  Number of practitioners:
                </label>
                <input
                  type="number"
                  min={0}
                  max={10}
                  value={practitionerCount[c.id] || 0}
                  onChange={(e) => {
                    const count = parseInt(e.target.value, 10);
                    setPractitionerCount((prev) => ({ ...prev, [c.id]: count }));
                    setPractitioners((prev) => ({
                      ...prev,
                      [c.id]: Array.from({ length: count }, (_, i) => prev[c.id]?.[i] || { full_name: "", role: "" }),
                    }));
                  }}
                  className="border rounded px-2 py-1 w-24 mb-4"
                />

                {practitioners[c.id]?.map((p, i) => (
                  <div key={i} className="flex gap-2 mb-2">
                    <input
                      type="text"
                      placeholder={`Name #${i + 1}`}
                      value={p.full_name}
                      onChange={(e) =>
                        setPractitioners((prev) => {
                          const updated = [...(prev[c.id] || [])];
                          updated[i].full_name = e.target.value;
                          return { ...prev, [c.id]: updated };
                        })
                      }
                      className="border px-2 py-1 rounded flex-1"
                    />
                    <input
                      type="text"
                      placeholder="Role"
                      value={p.role}
                      onChange={(e) =>
                        setPractitioners((prev) => {
                          const updated = [...(prev[c.id] || [])];
                          updated[i].role = e.target.value;
                          return { ...prev, [c.id]: updated };
                        })
                      }
                      className="border px-2 py-1 rounded w-40"
                    />
                  </div>
                ))}

                {practitionerCount[c.id] > 0 && (
                  <button
                    onClick={() => savePractitioners(c.id)}
                    className="mt-3 bg-green-600 text-white px-4 py-1.5 rounded-md text-sm"
                  >
                    Save practitioners
                  </button>
                )}

                {/* Liste des praticiens existants */}
                {existingPractitioners[c.id] && existingPractitioners[c.id].length > 0 && (
                  <div className="mt-6 border-t pt-4">
                    <h3 className="font-semibold text-gray-700 mb-2">
                      Existing practitioners
                    </h3>
                    <div className="space-y-2">
                      {existingPractitioners[c.id].map((p) => (
                        <div key={p.id} className="flex items-center gap-3">
                          <input
                            type="text"
                            value={p.full_name}
                            onChange={(e) => {
                              const updated = { ...p, full_name: e.target.value };
                              setExistingPractitioners((prev) => ({
                                ...prev,
                                [c.id]: prev[c.id].map((x) => (x.id === p.id ? updated : x)),
                              }));
                            }}
                            className="border px-2 py-1 rounded flex-1"
                          />
                          <input
                            type="text"
                            value={p.role}
                            onChange={(e) => {
                              const updated = { ...p, role: e.target.value };
                              setExistingPractitioners((prev) => ({
                                ...prev,
                                [c.id]: prev[c.id].map((x) => (x.id === p.id ? updated : x)),
                              }));
                            }}
                            className="border px-2 py-1 rounded w-40"
                          />
                          <button
                            onClick={() => updatePractitioner(p.id, p.full_name, p.role, c.id)}
                            className="text-green-600 border border-green-600 px-3 py-1 rounded-md text-xs"
                          >
                            Save
                          </button>
                          <button
                            onClick={() => deletePractitioner(p.id, c.id)}
                            className="text-red-600 border border-red-600 px-3 py-1 rounded-md text-xs"
                          >
                            Delete
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              )}

              {/* ── Section RBAC Admin ─────────────────────────────── */}
              {activeSettingsSection === "rbac" && existingPractitioners[c.id] && existingPractitioners[c.id].length > 0 && (
                <div className="mt-6 border-t pt-4">
                  <h3 className="font-semibold text-gray-700 mb-1">🔐 RBAC Admin</h3>
                  <p className="text-xs text-gray-400 mb-4">
                    Designate the clinic admin and reset staff passwords. Only relevant when RBAC is enabled.
                  </p>
                  <div className="space-y-3">
                    {existingPractitioners[c.id].map((p) => (
                      <div
                        key={p.id}
                        className="flex flex-wrap items-center gap-3 text-sm"
                      >
                        {/* Name */}
                        <span className="font-medium text-gray-700 min-w-[140px]">
                          {p.full_name}
                          {p.is_clinic_admin && (
                            <span className="ml-2 text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded font-medium">
                              Clinic admin
                            </span>
                          )}
                        </span>

                        {/* Set as clinic admin */}
                        <button
                          onClick={() => setClinicAdmin(p.id, c.id)}
                          disabled={p.is_clinic_admin || rbacAdminSaving === p.id}
                          className="text-xs border border-blue-400 text-blue-600 px-2 py-1 rounded disabled:opacity-40 hover:bg-blue-50"
                        >
                          {rbacAdminSaving === p.id ? "Saving…" : "Set as admin"}
                        </button>

                        {/* Reset password */}
                        <button
                          onClick={() => resetStaffPassword(p.id)}
                          disabled={rbacResetting === p.id}
                          className="text-xs border border-amber-400 text-amber-700 px-2 py-1 rounded disabled:opacity-40 hover:bg-amber-50"
                        >
                          {rbacResetting === p.id ? "Resetting…" : "Reset password"}
                        </button>

                        {/* Temp password display */}
                        {rbacTempPasswords[p.id] && (
                          <div className="flex items-center gap-1.5 bg-yellow-50 border border-yellow-200 rounded px-2 py-1">
                            <span className="font-mono text-xs text-yellow-800">
                              {rbacTempPasswords[p.id]}
                            </span>
                            <button
                              onClick={() => copyRbacPassword(p.id, rbacTempPasswords[p.id])}
                              className="text-yellow-600 hover:text-yellow-800"
                              title="Copy"
                            >
                              {rbacCopied === p.id ? (
                                <span className="text-xs text-green-600">✓</span>
                              ) : (
                                <span className="text-xs">📋</span>
                              )}
                            </button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              </>
              )}
              </div>
              </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Delete confirmation modal */}
      {deleteTarget && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 px-4">
          <div className="bg-white rounded-xl shadow-xl p-6 max-w-md w-full">
            <h2 className="text-lg font-semibold text-gray-800 mb-2">Supprimer ce client ?</h2>
            <p className="text-sm text-gray-600 mb-6">
              Vous êtes sur le point de supprimer{" "}
              <strong>{deleteTarget.raison_sociale}</strong>. Cette action est{" "}
              <span className="text-red-600 font-medium">irréversible</span> et effacera toutes les
              données associées : conversations, patients, clés de chiffrement, praticiens, templates,
              etc.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setDeleteTarget(null)}
                disabled={deleteLoading}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-60"
              >
                Annuler
              </button>
              <button
                onClick={() => confirmDelete(deleteTarget)}
                disabled={deleteLoading}
                className="px-4 py-2 bg-red-600 text-white rounded-md text-sm hover:bg-red-700 disabled:opacity-60 flex items-center gap-2"
              >
                {deleteLoading && (
                  <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                  </svg>
                )}
                {deleteLoading ? "Suppression…" : "Supprimer définitivement"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
